/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {
  useCallback,
  useRef
} from 'react';
import {ClickAwayListener, Paper} from '@material-ui/core';
import Button from '@material-ui/core/Button';
import Popper from '@material-ui/core/Popper';
import {makeStyles} from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import {translate} from '@ion/core/src/locale/locale';
import CloseButton from '@ion/dls/src/components/buttons/CloseButton';
import {GridClearFiltersButton} from '@ion/dls/src/components/buttons/GridClearFiltersButton';
import FormGroupFields from '@ion/dls/src/components/forms/FormGroupFields';
import {FormItem} from '@ion/dls/src/components/forms/FormItem';
import {FormTextField} from '@ion/dls/src/components/forms/FormTextField';
import TextField from '@ion/dls/src/components/textfield/TextField';
import {CancelButton} from '@ion/dls/src/editors/controls/CancelButton';
import {SaveButton} from '@ion/dls/src/editors/controls/SaveButton';
import {Hidden} from '@ion/dls/src/layout/Hidden';
import Render from '@ion/dls/src/render/Render';
import {toggleThemeStyle} from '@ion/dls/src/theme/helpers';
import {dlsColor, dlsShadow} from '@ion/dls/src/theme/theme';
import useThemeContext from '@ion/dls/src/theme/useThemeContext';
import {DLSTheme} from '@ion/dls/src/types/types';
import {QueryChips} from './QueryChips';
import {SearchOperationsDropDown} from './SearchOperationsDropDown';
import {
  CLEAR_ALL_BUTTON,
  queryChipsID,
  useQueryDialog
} from './useQueryDialog';

interface DisplayQueryChipsProps {
  queries: any;
  onQueryRemove: any;
  pageName: string;
  saveQuery?: Function;
  adornmentClass?: string;
  queryName?: string | null;
  onRemoveQuery?: Function;
  showFullWidthSearch?: boolean;
  isPhone?: boolean;
  onClearAll: () => void;
  onUndo: (queryName: string) => void;
  onClickQueryChip: (queryChipData: any) => void;
  onSavedQuerySelect: (args: any) => void;
  isGridInsideADialog?: boolean;
}

const useStyles = makeStyles((theme: DLSTheme) => {
  return {
    displayQueriesWrapper : toggleThemeStyle({
      display                        : 'flex',
      height                         : '40px',
      justifyContent                 : 'space-between',
      maxWidth                       : 720,
      width                          : '88%',
      [theme.breakpoints.down('sm')] : {
        height         : 'auto',
        width          : 'auto',
        flex           : 1,
        justifyContent : 'flex-end'
      }
    }, {
      gap                            : '8px',
      height                         : '32px',
      display                        : 'flex',
      borderRadius                   : '8px',
      [theme.breakpoints.down('sm')] : {
        flex           : 1,
        width          : 'auto',
        height         : 'auto',
        justifyContent : 'flex-end'
      }
    }),
    displayQueriesWrapperFullWidth : {
      display                        : 'flex',
      height                         : '40px',
      justifyContent                 : 'space-between',
      width                          : '100%',
      [theme.breakpoints.down('sm')] : {
        flex       : ({isGridInsideADialog}: {isGridInsideADialog: boolean}) => isGridInsideADialog ? 1 : 'unset',
        alignItems : ({isGridInsideADialog}: {isGridInsideADialog: boolean}) => isGridInsideADialog ? 'center' : 'auto',
        width      : ({isGridInsideADialog}: {isGridInsideADialog: boolean}) => isGridInsideADialog ? '25%' : '100%'
      }
    },
    nameAndChipsWrapper : {
      display : 'flex'
    },
    buttonWrapper : {
      display    : 'flex',
      marginTop  : toggleThemeStyle(4, 10),
      height     : '24px',
      alignItems : 'center'
    },
    saveText : {
      color       : dlsColor('aaBlue'),
      fontSize    : 12,
      height      : '18px',
      lineHeight  : '20px',
      paddingLeft : '4px',
      whiteSpace  : 'nowrap',
      '&:hover'   : {
        borderBottom : `1px solid ${dlsColor('caBlue')}` as string
      }
    },
    paper : toggleThemeStyle({
      height  : '150px',
      width   : '400px',
      padding : '20px 30px'
    }, {
      width           : '540px',
      height          : '310px',
      borderTop       : `2px solid ${dlsColor('pastelTeal')}`,
      boxShadow       : dlsShadow(2),
      borderRadius    : '12px',
      backgroundColor : dlsColor('white')
    }),
    poperTitle : toggleThemeStyle({
      display        : 'flex',
      justifyContent : 'space-between',
      padding        : '16px 10px',
      alignItems     : 'center',
      fontSize       : 16,
      fontFamily     : theme.typography.fontFamily
    }, {
      color          : dlsColor('grey1'),
      display        : 'flex !important',
      padding        : '16px 16px !important',
      fontSize       : '16px',
      alignItems     : 'center !important',
      fontFamily     : 'Inter !important',
      fontWeight     : 600,
      lineHeight     : '20px',
      letterSpacing  : '0px',
      justifyContent : 'space-between !important'
    }),
    button : {
      color    : dlsColor('aaBlue'),
      fontSize : 12
    },
    inputText : {
      fontSize   : 14,
      fontFamily : theme.typography.fontFamily
    },
    queryListWrapper : {
      display       : 'flex',
      flexDirection : 'row'
    },
    currentCloseButton : {
      right    : '19px',
      position : 'absolute'
    },
    contentText : {
      top           : '8px',
      color         : dlsColor('grey1'),
      display       : 'flex',
      padding       : '8px 16px 8px 16px',
      position      : 'relative',
      fontSize      : '16px',
      fontFamily    : 'Inter',
      fontWeight    : 400,
      lineHeight    : '20px',
      letterSpacing : '0px'
    },
    textFieldWrapper : {
      width      : '93%',
      marginTop  : '28px',
      marginLeft : '18px'
    },
    formActions : {
      display        : 'flex',
      marginTop      : '30px',
      alignItems     : 'center',
      justifyContent : 'flex-end'
    }
  };
});

const DisplayQueryChips = ({
  queries,
  onQueryRemove,
  saveQuery,
  adornmentClass,
  queryName = null,
  onRemoveQuery,
  pageName,
  onClearAll,
  onUndo,
  onClickQueryChip,
  onSavedQuerySelect,
  isPhone = false,
  showFullWidthSearch = false,
  isGridInsideADialog = false
}: DisplayQueryChipsProps) => {
  const classes       = useStyles({isGridInsideADialog});
  const saveButtonRef = useRef(null);

  const {
    isCurrent : isCurrentTheme,
    isLegacy  : isLegacyTheme
  } = useThemeContext();

  const {
    setCanShowSaveQueryDialog,
    canShowQueryOperations,
    canShowSaveQueryDialog,
    resetSavedQueryName,
    setSavedQueryName,
    savedQueryName,
    handleClose,
    onSaveClick
  } = useQueryDialog(queries);

  const onChange = useCallback(function({target: {value}}) {
    setSavedQueryName(value);
  }, []);

  const onTextClear = useCallback(function() {
    setSavedQueryName('');
  }, []);

  const onSave = useCallback(function() {
    setCanShowSaveQueryDialog(false);
    saveQuery?.(savedQueryName, resetSavedQueryName);
  }, [savedQueryName, queryName, queries]);

  const onRemove = useCallback(function() {
    onRemoveQuery?.(queryName);
  }, [queries, queryName]);

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <div ref={saveButtonRef}
        className={showFullWidthSearch ? classes.displayQueriesWrapperFullWidth : classes.displayQueriesWrapper}>
        <Hidden show='desktop'>
          <Render.If condition={isLegacyTheme}>
            <div className={classes.nameAndChipsWrapper}>
              <div id={queryChipsID}
                className={classes.queryListWrapper}>
                <QueryChips queries={queries}
                  onRemove={onQueryRemove}
                  showCount={2}
                  pageName={pageName}
                  onClickQueryChip={onClickQueryChip} />
              </div>
            </div>
          </Render.If>
        </Hidden>
        <div className={classes.buttonWrapper}>
          <Render.If condition={canShowQueryOperations && !isPhone && isLegacyTheme}>
            <Button id={CLEAR_ALL_BUTTON}
              onClick={onClearAll}
              style={{
                height          : 'inherit',
                backgroundColor : queries.length === 0 ? 'transparent' : 'inherit'
              }}>
              <span className={classes.saveText}>{translate('common:columnChooser.clear')}</span>
            </Button>
          </Render.If>
          {isPhone && (
            <GridClearFiltersButton onClearFilters={onClearAll} areFiltersApplied={canShowQueryOperations} />
          )}
          <Hidden show='desktop'>
            <SearchOperationsDropDown queryName={queryName}
              pageName={pageName}
              queries={queries}
              onSaveClick={onSaveClick}
              onSave={onSave}
              onUndoChanges={onUndo}
              onRemove={onRemove}
              onSavedQuerySelect={onSavedQuerySelect} />
          </Hidden>
        </div>
        <Popper open={canShowSaveQueryDialog}
          anchorEl={saveButtonRef.current}
          role={'dialog'}
          transition
          disablePortal
          placement='bottom-end'
          style={{zIndex: 100}}>
          <Paper classes={{root: classes.paper}}>
            <div className={classes.poperTitle}>
              <span>{translate('common:modularsearch.saveSearch')}</span>
              <Render>
                <Render.If condition={isLegacyTheme}>
                  <CloseIcon style={{cursor: 'pointer', height: 20, opacity: 0.5}} onClick={handleClose}/>
                </Render.If>
                <Render.Else>
                  <div className={classes.currentCloseButton}>
                    <CloseButton onClick={handleClose} />
                  </div>
                </Render.Else>
              </Render>
            </div>
            <Render.If condition={isCurrentTheme}>
              <span className={classes.contentText}>
                {translate('common:modularsearch.saveForSavedQuery')}
              </span>
            </Render.If>
            <Render>
              <Render.If condition={isLegacyTheme}>
                <TextField onChange={onChange}
                  placeholder={translate('common:modularsearch.saveForSavedQuery')}
                  value={savedQueryName}
                  inputProps={{maxLength: 32}}
                  InputProps={{
                    classes : {
                      input : classes.inputText
                    },
                    endAdornment : (<div className={adornmentClass}>
                      {Boolean(savedQueryName.length) && (<CloseIcon onClick={onTextClear}
                        style={{cursor: 'pointer', opacity: 0.2, height: '20px'}} />)}
                      <Button className={classes.button}
                        disabled={!savedQueryName}
                        variant='text'
                        onClick={onSave}>
                        {translate('common:editors.shared.save')}
                      </Button>
                    </div>)
                  }} />
              </Render.If>
              <Render.Else>
                <div className={classes.textFieldWrapper}>
                  <FormGroupFields>
                    <FormItem fullWidth>
                      <FormTextField required
                        fieldLabel={translate('common:modularsearch.searchName')}
                        placeholder={translate('common:modularsearch.enterName')}
                        onChange={onChange}
                        value={savedQueryName}
                        inputProps={{maxLength: 32}} />
                    </FormItem>
                  </FormGroupFields>
                </div>
              </Render.Else>
            </Render>
            <div className={classes.formActions}>
              <CancelButton onCancel={handleClose} />
              <SaveButton onSave={onSave}
                label={translate('common:editors.shared.save')}
                disabled={!savedQueryName} />
            </div>
          </Paper>
        </Popper>
      </div>
    </ClickAwayListener>
  );
};

export default DisplayQueryChips;
